import { type } from 'arktype'

import { amountToString } from '@orus.eu/amount'
import { nbsp } from '@orus.eu/char'
import {
  AbstractDimension,
  BooleanDimension,
  CalendarDateDimension,
  type DimensionValidationProblem,
  dimensionValidationFailure,
  ExclusionDimension,
  FinancialRateDimension,
  FinancialRateWithUnsetDimension,
  frMrpaxeSelectedDimension,
  type LooselyTypedValue,
  StringsEnumDimension,
  StringsWithDataEnumDimension,
} from '@orus.eu/dimensions'
import { frMrpaxeProtectionLevels } from '@orus.eu/pricing'
import {
  type FrMrpaxeActivityClass,
  type FrMrpaxeDerogationAssetsValue,
  type FrMrpaxeDerogationBrokenMaterial,
  type FrMrpaxeDerogationCashAndValues,
  type FrMrpaxeDerogationGeneralDeductible,
  type FrMrpaxeDerogationOptionBdgValue,
  type FrMrpaxeDerogationOptionPeValue,
  type FrMrpaxeDerogationOutdoorFurniture,
  type FrMrpaxeDerogationPreservedGoods,
  type FrMrpaxeDerogationRealEstate,
  type FrMrpaxeDerogationStolenAssetsValue,
  frMrpaxeActivityClasses,
  frMrpaxeActivityClassLabels,
  frMrpaxeDerogationAssetsValueType,
  frMrpaxeProductVersions,
  frMrpaxeSegments,
} from '@orus.eu/product'
import { type Result, success } from '@orus.eu/result'

export const frMrpaxeProductVersionDimension = new StringsEnumDimension({
  name: 'frMrpaxeProductVersion',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_product_version' },
  values: frMrpaxeProductVersions,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxeSegmentDimension = new StringsEnumDimension({
  name: 'frMrpaxeSegment',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_segment' },
  values: frMrpaxeSegments,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

/** The theft protection level the RETA agreed terms require. The tariff returns it with the price. */
export const frMrpaxeRequiredProtectionLevelDimension = new StringsEnumDimension({
  name: 'frMrpaxeRequiredProtectionLevel',
  displayKeys: {
    name: 'dimension_name_fr_mrpaxe_required_protection_level',
    hint: 'dimension_hint_fr_mrpaxe_required_protection_level',
  },
  values: frMrpaxeProtectionLevels,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

const activityClassEntries = frMrpaxeActivityClasses.map(
  (activityClass): [FrMrpaxeActivityClass, { label: string }] => [
    activityClass,
    { label: frMrpaxeActivityClassLabels[activityClass] },
  ],
)

export const frMrpaxeActivityClassDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeActivityClass',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_activity_class' },
  entries: activityClassEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

/**
 * The value of a secondary or tertiary activity the business does not have. A key with no value counts as a missing
 * input, which blocks the agreed terms, so the absence has a value of its own.
 */
export const FR_MRPAXE_NO_ADDITIONAL_ACTIVITY = 'none'

const additionalActivityEntries: [string, { label: string }][] = [
  [FR_MRPAXE_NO_ADDITIONAL_ACTIVITY, { label: 'Aucune' }],
  ...activityClassEntries,
]

/**
 * The activity class a secondary or tertiary activity dimension holds, if the business has one. The back office picks
 * the class itself, so a fast food can be one with cooking equipment.
 */
export function getFrMrpaxeAdditionalActivityClass(
  value: string | null | undefined,
): FrMrpaxeActivityClass | undefined {
  return frMrpaxeActivityClasses.find((activityClass) => activityClass === value)
}

/** An activity class the business also carries on, among the ones fr-mrpaxe insures. */
export const frMrpaxeSecondaryActivityDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeSecondaryActivity',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_secondary_activity' },
  entries: additionalActivityEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxeTertiaryActivityDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeTertiaryActivity',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_tertiary_activity' },
  entries: additionalActivityEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxeAssetsClaimsThreeYearsDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeAssetsClaimsThreeYears',
  displayKeys: {
    name: 'dimension_name_fr_mrpaxe_assets_claims_three_years',
    hint: 'dimension_hint_fr_mrpaxe_assets_claims_three_years',
  },
  entries: [
    ['0', { label: '0' }],
    ['1', { label: '1' }],
    ['2', { label: '2' }],
    ['3+', { label: '3+' }],
  ],
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxeOccupationStatusDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeOccupationStatus',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_occupation_status' },
  entries: [
    ['owner', { label: 'Propriétaire exploitant' }],
    ['tenant', { label: 'Locataire' }],
  ],
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxeMonumentDimension = new BooleanDimension({
  name: 'frMrpaxeMonument',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_monument' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxeHardBuildingDimension = new BooleanDimension({
  name: 'frMrpaxeHardBuilding',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_hard_building' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxeRcTravauxStartDimension = new CalendarDateDimension({
  name: 'frMrpaxeRcTravauxStart',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_rc_travaux_start' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxeRcTravauxEndDimension = new CalendarDateDimension({
  name: 'frMrpaxeRcTravauxEnd',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_rc_travaux_end' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

const premiumOverchargeConfiguration = { min: -0.1, max: 5, step: 0.01 } as const

export const frMrpaxePremiumOverchargesRcDimension = new FinancialRateDimension({
  name: 'frMrpaxePremiumOverchargesRC',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_premium_overcharges_rc' },
  configuration: premiumOverchargeConfiguration,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxePremiumOverchargesDmgDimension = new FinancialRateDimension({
  name: 'frMrpaxePremiumOverchargesDMG',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_premium_overcharges_dmg' },
  configuration: premiumOverchargeConfiguration,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxePremiumOverchargesIncPePvvDimension = new FinancialRateDimension({
  name: 'frMrpaxePremiumOverchargesIncPePvv',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_premium_overcharges_inc_pe_pvv' },
  configuration: premiumOverchargeConfiguration,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

//#region Fees
export const frMrpaxeDefaultManagementFeeRateDimension = new FinancialRateDimension({
  name: 'frMrpaxeDefaultManagementFeeRate',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_default_management_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const frMrpaxeManuallySetManagementFeeRateDimension = new FinancialRateWithUnsetDimension({
  name: 'frMrpaxeManuallySetManagementFeeRate',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_manually_set_management_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const frMrpaxeManagementFeeRateDimension = new FinancialRateDimension({
  name: 'frMrpaxeManagementFeeRate',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_management_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxeDefaultInstallmentFeeRateDimension = new FinancialRateDimension({
  name: 'frMrpaxeDefaultInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_default_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const frMrpaxeManuallySetInstallmentFeeRateDimension = new FinancialRateWithUnsetDimension({
  name: 'frMrpaxeManuallySetInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_manually_set_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const frMrpaxeInstallmentFeeRateDimension = new FinancialRateDimension({
  name: 'frMrpaxeInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
//#endregion Fees

/** True when an underwriter waives the 2025 majoration of the tariff. */
export const frMrpaxeHasDerogationMajorationDimension = new BooleanDimension({
  name: 'frMrpaxeHasDerogationMajoration',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_has_derogation_majoration' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export class FrMrpaxeDerogationAssetsValueDimension<NAME extends string> extends AbstractDimension<
  NAME,
  FrMrpaxeDerogationAssetsValue
> {
  override validateData(value: LooselyTypedValue): Result<FrMrpaxeDerogationAssetsValue, DimensionValidationProblem> {
    const result = frMrpaxeDerogationAssetsValueType(value)
    if (result instanceof type.errors) {
      return dimensionValidationFailure(`Field ${this.name} is not valid: ${result.summary}`)
    }
    return success(result)
  }

  override formatToString(value: FrMrpaxeDerogationAssetsValue): string | undefined {
    return value.type === 'amount' ? amountToString(value.amount) : 'DEFAULT'
  }
}

export const frMrpaxeDerogationAssetsValueDimension = new FrMrpaxeDerogationAssetsValueDimension({
  name: 'frMrpaxeDerogationAssetsValue',
  displayKeys: {
    name: 'dimension_name_fr_mrpaxe_derogation_assets_value',
    placeholder: 'dimension_placeholder_fr_mrpaxe_derogation_assets_value',
  },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

const generalDeductibleEntries: [FrMrpaxeDerogationGeneralDeductible, { label: string }][] = [
  ['DEFAULT', { label: `450${nbsp}€ (par défaut)` }],
  ['1 000', { label: `1 000${nbsp}€` }],
  ['1 500', { label: `1 500${nbsp}€` }],
]

export const frMrpaxeDerogationGeneralDeductibleDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeDerogationGeneralDeductible',
  displayKeys: {
    name: 'dimension_name_fr_mrpaxe_derogation_general_deductible',
    placeholder: 'dimension_placeholder_fr_mrpaxe_derogation_general_deductible',
  },
  entries: generalDeductibleEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

const stolenAssetsValueEntries: [FrMrpaxeDerogationStolenAssetsValue, { label: string }][] = [
  ['10%', { label: `10${nbsp}%` }],
  ['20%', { label: `20${nbsp}%` }],
  ['DEFAULT', { label: `50${nbsp}% (par défaut)` }],
  ['75%', { label: `75${nbsp}%` }],
  ['100%', { label: `100${nbsp}%` }],
]

export const frMrpaxeDerogationStolenAssetsValueDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeDerogationStolenAssetsValue',
  displayKeys: {
    name: 'dimension_name_fr_mrpaxe_derogation_stolen_assets_value',
    placeholder: 'dimension_placeholder_fr_mrpaxe_derogation_stolen_assets_value',
  },
  entries: stolenAssetsValueEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

const cashAndValuesEntries: [FrMrpaxeDerogationCashAndValues, { label: string }][] = [
  ['DEFAULT', { label: `3 000${nbsp}€ (par défaut)` }],
  ['10 000', { label: `10 000${nbsp}€` }],
  ['15 000', { label: `15 000${nbsp}€` }],
  ['20 000', { label: `20 000${nbsp}€` }],
]

export const frMrpaxeDerogationCashAndValuesDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeDerogationCashAndValues',
  displayKeys: {
    name: 'dimension_name_fr_mrpaxe_derogation_cash_and_values',
    placeholder: 'dimension_placeholder_fr_mrpaxe_derogation_cash_and_values',
  },
  entries: cashAndValuesEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

const brokenMaterialEntries: [FrMrpaxeDerogationBrokenMaterial, { label: string }][] = [
  ['DEFAULT', { label: `3 000${nbsp}€ (par défaut, 5 000${nbsp}€ avec BOOST)` }],
  ['10 000', { label: `10 000${nbsp}€` }],
  ['20 000', { label: `20 000${nbsp}€` }],
  ['30 000', { label: `30 000${nbsp}€` }],
]

export const frMrpaxeDerogationBrokenMaterialDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeDerogationBrokenMaterial',
  displayKeys: {
    name: 'dimension_name_fr_mrpaxe_derogation_broken_material',
    placeholder: 'dimension_placeholder_fr_mrpaxe_derogation_broken_material',
  },
  entries: brokenMaterialEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

const preservedGoodsEntries: [FrMrpaxeDerogationPreservedGoods, { label: string }][] = [
  ['5%', { label: `5${nbsp}% du CA annuel HT` }],
  ['10%', { label: `10${nbsp}% du CA annuel HT` }],
  ['DEFAULT', { label: `15${nbsp}% du CA annuel HT (par défaut)` }],
  ['20%', { label: `20${nbsp}% du CA annuel HT` }],
]

export const frMrpaxeDerogationPreservedGoodsDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeDerogationPreservedGoods',
  displayKeys: {
    name: 'dimension_name_fr_mrpaxe_derogation_preserved_goods',
    placeholder: 'dimension_placeholder_fr_mrpaxe_derogation_preserved_goods',
  },
  entries: preservedGoodsEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

const optionPeValueEntries: [FrMrpaxeDerogationOptionPeValue, { label: string }][] = [
  ['DEFAULT', { label: '12 mois (par défaut)' }],
  ['18 mois', { label: '18 mois' }],
  ['24 mois', { label: '24 mois' }],
]

export const frMrpaxeDerogationOptionPeValueDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeDerogationOptionPeValue',
  displayKeys: {
    name: 'dimension_name_fr_mrpaxe_derogation_option_pe_value',
    placeholder: 'dimension_placeholder_fr_mrpaxe_derogation_option_pe_value',
  },
  entries: optionPeValueEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

const optionBdgValueEntries: [FrMrpaxeDerogationOptionBdgValue, { label: string }][] = [
  ['DEFAULT', { label: `8 000${nbsp}€ (par défaut)` }],
  ['15 000', { label: `15 000${nbsp}€` }],
  ['20 000', { label: `20 000${nbsp}€` }],
  ['30 000', { label: `30 000${nbsp}€` }],
]

export const frMrpaxeDerogationOptionBdgValueDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeDerogationOptionBdgValue',
  displayKeys: {
    name: 'dimension_name_fr_mrpaxe_derogation_option_bdg_value',
    placeholder: 'dimension_placeholder_fr_mrpaxe_derogation_option_bdg_value',
  },
  entries: optionBdgValueEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

const outdoorFurnitureEntries: [FrMrpaxeDerogationOutdoorFurniture, { label: string }][] = [
  ['DEFAULT', { label: `3 500${nbsp}€ (par défaut, 5 000${nbsp}€ avec BOOST)` }],
  ['5 000', { label: `5 000${nbsp}€` }],
  ['10 000', { label: `10 000${nbsp}€` }],
  ['20 000', { label: `20 000${nbsp}€` }],
]

export const frMrpaxeDerogationOutdoorFurnitureDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeDerogationOutdoorFurniture',
  displayKeys: {
    name: 'dimension_name_fr_mrpaxe_derogation_outdoor_furniture',
    placeholder: 'dimension_placeholder_fr_mrpaxe_derogation_outdoor_furniture',
  },
  entries: outdoorFurnitureEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

const realEstateEntries: [FrMrpaxeDerogationRealEstate, { label: string }][] = [
  ['DEFAULT', { label: `20 000${nbsp}€ (par défaut, 20${nbsp}% du contenu si plus élevé avec BOOST)` }],
  ['30 000', { label: `30 000${nbsp}€` }],
  ['40 000', { label: `40 000${nbsp}€` }],
  ['50 000', { label: `50 000${nbsp}€` }],
]

export const frMrpaxeDerogationRealEstateDimension = new StringsWithDataEnumDimension({
  name: 'frMrpaxeDerogationRealEstate',
  displayKeys: {
    name: 'dimension_name_fr_mrpaxe_derogation_real_estate',
    placeholder: 'dimension_placeholder_fr_mrpaxe_derogation_real_estate',
  },
  entries: realEstateEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxeHasModularityDerogationDimension = new BooleanDimension({
  name: 'frMrpaxeHasModularityDerogation',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_has_modularity_derogation' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxeNewlyCreatedCompanyDimension = new BooleanDimension({
  name: 'frMrpaxeNewlyCreatedCompany',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_newly_created_company' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

/** The liability during works option is taken, so the works dates are asked. */
export const frMrpaxeHasLiabilityDuringWorksDimension = new BooleanDimension({
  name: 'frMrpaxeHasLiabilityDuringWorks',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_has_liability_during_works' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxeManagementExperienceInPizzeriaRequiredDimension = new BooleanDimension({
  name: 'frMrpaxeManagementExperienceInPizzeriaRequired',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_management_experience_in_pizzeria_required' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frMrpaxeHasManagementExperienceInPizzeriaDimension = new BooleanDimension({
  name: 'frMrpaxeHasManagementExperienceInPizzeria',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_has_management_experience_in_pizzeria' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: { lockedInQuoteFor: { client: frMrpaxeSelectedDimension } },
} as const)

export const frMrpaxeExclusionsDimension = new ExclusionDimension({
  name: 'frMrpaxeExclusions',
  displayKeys: { name: 'dimension_name_fr_mrpaxe_exclusions' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
